/** 难度等级与智能分割：根据原图宽高比计算 rows/cols，让每块尽量接近正方形。 */

export type DifficultyLevel = 'easy' | 'medium' | 'hard' | 'expert';

/** 各难度目标总块数（实际 rows×cols 会尽量接近且每块接近正方形）。 */
export const DIFFICULTY_TARGET: Record<DifficultyLevel, number> = {
  easy: 12,
  medium: 30,
  hard: 65,
  expert: 140,
};

export const DIFFICULTY_LABEL: Record<DifficultyLevel, string> = {
  easy: '简单',
  medium: '中',
  hard: '难',
  expert: '专家',
};

export const DIFFICULTY_ORDER: DifficultyLevel[] = ['easy', 'medium', 'hard', 'expert'];

export interface Grid {
  rows: number;
  cols: number;
}

/**
 * 根据原图宽高比与目标块数，计算 rows/cols，使每块尽量接近正方形。
 *
 * 原理：每块宽高比 = (imgW/cols) / (imgH/rows) = (imgW/imgH) * (rows/cols)。
 * 要每块接近正方形 → cols/rows ≈ imgW/imgH（长边多拆）。
 *
 * 枚举 cols ∈ [2,20]，取「块形最方正 + 总数最接近目标」综合最优。
 */
export function computeGrid(imgW: number, imgH: number, target: number): Grid {
  const r = imgW / imgH;
  let best: Grid & { score: number } = { rows: 2, cols: 2, score: Infinity };

  // rows/cols 自由枚举（不锁死总数），取「每块最方正 + 总数最接近目标」综合最优
  for (let cols = 2; cols <= 20; cols++) {
    for (let rows = 2; rows <= 20; rows++) {
      const pieceRatio = r * (rows / cols); // 每块宽/高
      const ratioScore = Math.abs(Math.log(pieceRatio)); // 偏离正方形的程度（0=正方形）
      const countScore = Math.abs(rows * cols - target) / target; // 总数偏离目标
      const score = ratioScore * 2 + countScore; // 正方形权重更高
      if (score < best.score) best = { rows, cols, score };
    }
  }
  return { rows: best.rows, cols: best.cols };
}

/** 按难度等级取分割。 */
export function gridForDifficulty(
  imgW: number,
  imgH: number,
  level: DifficultyLevel,
): Grid {
  return computeGrid(imgW, imgH, DIFFICULTY_TARGET[level]);
}
